import React from 'react';
import { connect } from 'react-redux';

import {
  Avatar,
  LandingPageIntro,
  LayoutSingleColumn,
  NamedLink,
  ResponsiveImage,
  ReviewRating,
} from '../../components';
import { useConfiguration } from '../../context/configurationContext';
import { createSlug } from '../../util/urlHelpers';
import { getSellerShopName } from '../../util/userHelpers';
import { FormattedMessage } from '../../util/reactIntl';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import StaticPage from '../PageBuilder/StaticPage';
import DiscoverLandingPage from '../DiscoverLandingPage/DiscoverLandingPage';

import {
  getListingPreview,
  editionSourceHighlights,
  newestBookListings,
  rankAuthorsByActiveListings,
  rankSeriesByActiveListings,
  rankShopsByProviderReviews,
  trendingBooksByRepeatedAuthors,
} from './TrendingPage.duck';
import css from './TrendingPage.module.css';
import BookCover from './BookCover';

const pages = {
  hub: {
    title: 'The Shelf Edit',
    intro: 'Seasonal lists, special editions, themed reads and PageHop picks.',
  },
  books: {
    title: 'Trending Books',
    intro: 'Books by authors with repeated active PageHop listings.',
  },
  new: {
    title: 'New Arrivals',
    intro: 'Recently added books from PageHop sellers.',
  },
  bestsellers: {
    title: 'Bestsellers',
    intro: 'Books PageHop readers are buying right now.',
    emptyTitle: 'Not enough sales activity yet',
    emptyCopy:
      'Bestseller rankings need completed marketplace transactions. PageHop does not expose a safe public sales aggregation for this page yet.',
  },
  authors: {
    title: 'Popular Authors',
    intro: 'Authors with the most active PageHop listings right now.',
  },
  'author-spotlight': {
    title: 'Author Spotlight',
    intro:
      'The Canadian author we can’t stop recommending, plus writers showing up across PageHop.',
    emptyTitle: 'No spotlight published yet',
    emptyCopy:
      'Author Spotlight follows the PageHop editorial calendar. A new author feature appears here when the next issue is published.',
  },
  'curated-reads': {
    title: 'The Shelf Edit',
    intro: 'Seasonal lists, special editions, themed reads and PageHop picks.',
  },
  series: {
    title: 'Popular Series',
    intro: 'Series with enough structured PageHop listing data to browse reliably.',
    emptyTitle: 'No series data yet',
    emptyCopy:
      'Popular Series needs a structured series field on listings. PageHop will show this once enough listings include reliable series metadata.',
  },
  'highly-rated-shops': {
    title: 'Highly Rated Shops',
    intro: 'Seller Shops with public provider reviews from PageHop transactions.',
    emptyTitle: 'Not enough shop reviews yet',
    emptyCopy:
      'Highly Rated Shops appears once sellers have public provider reviews. Seller ratings are kept separate from book ratings.',
  },
};

const ListingTile = ({ listing }) => {
  const config = useConfiguration();
  const preview = getListingPreview(listing);
  const image = listing.images?.[0];
  const { variantPrefix = 'listing-card' } = config.layout?.listingImage || {};
  const variants = image
    ? Object.keys(image.attributes?.variants || {}).filter(name => name.startsWith(variantPrefix))
    : [];
  const slug = createSlug(preview.title);

  return (
    <NamedLink
      className={css.listingTile}
      name="ListingPage"
      params={{ id: listing.id.uuid, slug }}
    >
      <span className={css.listingImageFrame}>
        <ResponsiveImage
          rootClassName={css.listingImage}
          image={image}
          variants={variants}
          sizes="(max-width: 767px) 42vw, (max-width: 1023px) 28vw, 220px"
          alt={
            preview.author
              ? `Cover of ${preview.title} by ${preview.author}`
              : `Cover of ${preview.title}`
          }
        />
      </span>
      <span className={css.listingTitle}>{preview.title}</span>
      {preview.author ? <span className={css.listingAuthor}>{preview.author}</span> : null}
    </NamedLink>
  );
};

const SectionHeader = ({ id, title, description, linkName, linkText = 'View all' }) => (
  <header className={css.sectionHeader}>
    <div>
      <h2 id={id}>{title}</h2>
      {description ? <p>{description}</p> : null}
    </div>
    {linkName ? <NamedLink name={linkName}>{linkText}</NamedLink> : null}
  </header>
);

const ListingSection = ({ id, title, description, listings, linkName, linkText }) =>
  listings.length ? (
    <section className={css.discoverySection} aria-labelledby={id}>
      <SectionHeader
        id={id}
        title={title}
        description={description}
        linkName={linkName}
        linkText={linkText}
      />
      <div className={css.listingGrid}>
        {listings.slice(0, 5).map(listing => (
          <ListingTile key={listing.id.uuid} listing={listing} />
        ))}
      </div>
    </section>
  ) : null;

const EditorialBookCard = ({ book }) => {
  const releaseDate = book.releaseDate ? new Date(`${book.releaseDate}T12:00:00`) : null;
  const releaseLabel =
    releaseDate && !Number.isNaN(releaseDate.getTime())
      ? releaseDate <= new Date()
        ? 'Out now'
        : releaseDate.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })
      : null;
  const sourceUrl = book.sources?.[0]?.url;
  const shelfySearch = `?keywords=${encodeURIComponent(book.title)}`;

  return (
    <article className={css.editorialBookCard}>
      <div className={css.editorialCover}>
        <NamedLink name="SearchPage" to={{ search: shelfySearch }}>
          <BookCover title={book.title} author={book.author} coverUrl={book.cover?.resolvedUrl} />
        </NamedLink>
      </div>
      <div className={css.editorialMeta}>
        {releaseLabel ? <span className={css.editorialStatus}>{releaseLabel}</span> : null}
        <h3>
          <NamedLink name="SearchPage" to={{ search: shelfySearch }}>
            {book.title}
          </NamedLink>
        </h3>
        <p>{book.author}</p>
        <span className={css.editorialGenre}>{book.genre}</span>
        <small>{book.note}</small>
        {sourceUrl ? (
          <a href={sourceUrl} target="_blank" rel="noreferrer">
            Source details
          </a>
        ) : null}
      </div>
    </article>
  );
};

const EditorialSection = ({ id, title, eyebrow, description, books, className }) =>
  books.length ? (
    <section className={`${css.editorialSection} ${className || ''}`} aria-labelledby={id}>
      <header className={css.editorialSectionHeader}>
        {eyebrow ? <p className={css.editorialEyebrow}>{eyebrow}</p> : null}
        <h2 id={id}>{title}</h2>
        <p>{description}</p>
      </header>
      <div className={css.editorialBookGrid}>
        {books.map(book => (
          <EditorialBookCard key={`${book.title}-${book.author}`} book={book} />
        ))}
      </div>
    </section>
  ) : null;

const ShelfRadarHero = ({ feature }) => (
  <section className={css.radarHero} aria-labelledby="shelf-radar-feature-title">
    <div className={css.radarHeroCopy}>
      <p className={css.editorialEyebrow}>{feature.eyebrow}</p>
      <h2 id="shelf-radar-feature-title">{feature.title}</h2>
      <p>{feature.description}</p>
      <NamedLink name={feature.ctaRoute} params={feature.ctaParams} className={css.radarHeroCta}>
        {feature.ctaLabel}
      </NamedLink>
    </div>
    <div className={css.radarHeroBooks}>
      {feature.books.map(book => (
        <div key={book.title} className={css.radarHeroBook}>
          <div className={css.radarHeroCover}>
            <BookCover title={book.title} author={book.author} coverUrl={book.cover?.resolvedUrl} />
          </div>
          <div className={css.radarHeroCaption}>
            <strong>{book.title}</strong>
            <span>{book.supportingLine}</span>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const AuthorSpotlight = ({ spotlight }) => (
  <section className={css.authorSpotlight} aria-labelledby="author-spotlight-title">
    <div>
      <p className={css.editorialEyebrow}>Canadian Author Spotlight</p>
      <h2 id="author-spotlight-title">Canadian Author Spotlight</h2>
      <h3>{spotlight.author}</h3>
      <p>{spotlight.intro}</p>
      <p className={css.spotlightKicker}>Get ready to make some shelf space.</p>
      <p>{spotlight.whyNow}</p>
      <a href={spotlight.ctaUrl} target="_blank" rel="noreferrer">
        {spotlight.ctaLabel}
      </a>
    </div>
    <div className={css.spotlightBooks}>
      {spotlight.books.map(book => (
        <div key={book.title} className={css.spotlightBook}>
          <BookCover title={book.title} author={book.author} coverUrl={book.cover?.resolvedUrl} />
        </div>
      ))}
    </div>
  </section>
);

const AuthorsToWatch = ({ listings }) => {
  const authors = rankAuthorsByActiveListings(listings).slice(0, 4);
  return authors.length ? (
    <section className={css.discoverySection} aria-labelledby="authors-to-watch-title">
      <SectionHeader
        id="authors-to-watch-title"
        title="Authors to Watch"
        description="Writers showing up across PageHop right now."
        linkName="TrendingAuthorsPage"
      />
      <div className={css.authorGrid}>
        {authors.map(author => (
          <NamedLink
            key={author.name}
            className={css.authorTile}
            name="SearchPage"
            to={{ search: `?keywords=${encodeURIComponent(author.name)}` }}
          >
            <PreviewImages listings={author.listings} label={`${author.name} listing previews`} />
            <strong>{author.name}</strong>
            <span>
              {author.listings.length} active listing{author.listings.length === 1 ? '' : 's'}
            </span>
          </NamedLink>
        ))}
      </div>
    </section>
  ) : null;
};

const CollectorBuzz = ({ listings }) => {
  const config = useConfiguration();
  const sourceField = config.listing?.listingFields?.find(field => field.key === 'editionsource');
  const labelForSource = source =>
    sourceField?.enumOptions?.find(option => option.option === source)?.label || source;
  const highlights = editionSourceHighlights(listings).slice(0, 3);
  return highlights.length ? (
    <section className={css.discoverySection} aria-labelledby="collector-buzz-title">
      <SectionHeader
        id="collector-buzz-title"
        title="Collector Radar"
        description="Edition sources and collector favourites appearing across the shelf."
      />
      <div className={css.buzzGrid}>
        {highlights.map(highlight => (
          <div key={highlight.source} className={css.buzzTile}>
            <PreviewImages
              listings={highlight.listings}
              label={`${labelForSource(highlight.source)} listing previews`}
            />
            <strong>{labelForSource(highlight.source)}</strong>
            <span>Collector editions currently listed on PageHop.</span>
          </div>
        ))}
      </div>
    </section>
  ) : null;
};

const TrendingHub = ({ editorial }) => {
  const currentEditorial = editorial || {};
  return (
    <div className={css.editorialHub}>
      {currentEditorial.heroFeatures?.[0] ? (
        <ShelfRadarHero feature={currentEditorial.heroFeatures[0]} />
      ) : null}
      {currentEditorial.canadianShelf?.length ? (
        <EditorialSection
          id="closer-to-home-title"
          title="Closer to Home"
          description="Canadian books, authors, and literary things on our radar."
          books={currentEditorial.canadianShelf}
          className={css.closerToHome}
        />
      ) : null}
      {currentEditorial.authorSpotlight ? (
        <AuthorSpotlight spotlight={currentEditorial.authorSpotlight} />
      ) : null}
      <EditorialSection
        id="new-this-month-title"
        title="New Releases"
        description="Editorially selected books published in the last 30 days."
        books={currentEditorial.newThisMonth || []}
        className={css.newThisMonth}
      />
      <EditorialSection
        id="coming-next-month-title"
        title="Coming Soon"
        description="A few releases worth watching."
        books={currentEditorial.comingNextMonth || []}
      />
    </div>
  );
};

const PreviewImages = ({ listings, label }) => (
  <div className={css.previewImages} aria-label={label}>
    {listings.slice(0, 3).map(listing => {
      const preview = getListingPreview(listing);
      return preview.imageUrl ? <img key={preview.id} src={preview.imageUrl} alt="" /> : null;
    })}
  </div>
);

const EmptyState = ({ title, copy }) => (
  <div className={css.emptyState} role="status">
    <h2>{title}</h2>
    <p>{copy}</p>
    <NamedLink name="SearchPage">Browse the marketplace</NamedLink>
  </div>
);

const hasCuratedEditorial = editorial =>
  Boolean(
    editorial?.heroFeatures?.length ||
      editorial?.canadianShelf?.length ||
      editorial?.authorSpotlight ||
      editorial?.newThisMonth?.length ||
      editorial?.comingNextMonth?.length
  );

const CuratedReadsEmptyState = ({ loading, error }) => {
  if (loading && !error) {
    return (
      <p className={css.curatedLoading} role="status">
        <FormattedMessage id="TrendingPage.curatedLoading" />
      </p>
    );
  }

  return (
    <section className={css.emptyState} role={error ? 'alert' : 'status'}>
      <h2>
        <FormattedMessage
          id={error ? 'TrendingPage.curatedErrorTitle' : 'TrendingPage.curatedEmptyTitle'}
        />
      </h2>
      <p>
        <FormattedMessage
          id={
            error ? 'TrendingPage.curatedErrorDescription' : 'TrendingPage.curatedEmptyDescription'
          }
        />
      </p>
      <div className={css.emptyStateActions}>
        <NamedLink name="TrendingPage">
          <FormattedMessage id="TrendingPage.backToDiscover" />
        </NamedLink>
        <NamedLink name="SearchPage">
          <FormattedMessage id="TrendingPage.browseMarketplace" />
        </NamedLink>
      </div>
    </section>
  );
};

const AuthorCard = ({ item, rank }) => (
  <article className={css.trendingCard}>
    <span className={css.rank}>{rank}</span>
    <div className={css.cardBody}>
      <h2>{item.name}</h2>
      <p>
        {item.listings.length} active listing{item.listings.length === 1 ? '' : 's'}
      </p>
      <PreviewImages listings={item.listings} label={`${item.name} listing previews`} />
      <NamedLink name="SearchPage" to={{ search: `?keywords=${encodeURIComponent(item.name)}` }}>
        Browse {item.name}
      </NamedLink>
    </div>
  </article>
);

const SeriesCard = ({ item, rank }) => (
  <article className={css.trendingCard}>
    <span className={css.rank}>{rank}</span>
    <div className={css.cardBody}>
      <h2>{item.name}</h2>
      <p>
        {item.listings.length} active listing{item.listings.length === 1 ? '' : 's'}
      </p>
      <PreviewImages listings={item.listings} label={`${item.name} listing previews`} />
      <NamedLink name="SearchPage" to={{ search: `?keywords=${encodeURIComponent(item.name)}` }}>
        Browse series
      </NamedLink>
    </div>
  </article>
);

const ShopCard = ({ item, rank }) => {
  const { seller, listings, reviews, rating } = item;
  const profile = seller.attributes?.profile || {};
  const publicData = profile.publicData || {};
  const shopName = getSellerShopName(seller, profile.displayName) || profile.displayName;

  return (
    <article className={css.shopCard}>
      <span className={css.rank}>{rank}</span>
      <div className={css.shopHeader}>
        <Avatar className={css.avatar} user={seller} disableProfileLink />
        <div>
          <h2>{shopName}</h2>
          {publicData.province ? <p>{publicData.province}</p> : null}
        </div>
      </div>
      <div
        className={css.rating}
        aria-label={`${rating.toFixed(1)} out of 5 from ${reviews.length} reviews`}
      >
        <ReviewRating rating={Math.round(rating)} />
        <span>
          {rating.toFixed(1)} ({reviews.length})
        </span>
      </div>
      {Array.isArray(publicData.shopSpecialties) && publicData.shopSpecialties.length ? (
        <div className={css.chips}>
          {publicData.shopSpecialties.slice(0, 3).map(specialty => (
            <span key={specialty}>{specialty}</span>
          ))}
        </div>
      ) : null}
      <PreviewImages listings={listings} label={`${shopName} listing previews`} />
      <NamedLink name="ShopPage" params={{ id: seller.id.uuid }}>
        View Shop
      </NamedLink>
    </article>
  );
};

const Results = ({
  page,
  listings,
  reviewsByUserId,
  editorial,
  editorialError,
  loading,
  error,
}) => {
  // /trending renders Discover; the full Shelf Edit editorial lives at /trending/curated-reads.
  if (page === 'curated-reads') {
    return hasCuratedEditorial(editorial) ? (
      <TrendingHub editorial={editorial} />
    ) : (
      <CuratedReadsEmptyState loading={loading} error={editorialError || Boolean(error)} />
    );
  }
  if (page === 'hub') return <TrendingHub editorial={editorial} />;

  if (page === 'books') {
    return (
      <ListingSection
        id="trending-books-title"
        title="Trending Books"
        listings={trendingBooksByRepeatedAuthors(listings)}
      />
    );
  }

  if (page === 'new') {
    return (
      <ListingSection
        id="new-books-title"
        title="New Arrivals"
        listings={newestBookListings(listings)}
      />
    );
  }
  if (page === 'bestsellers') {
    return <EmptyState title={pages.bestsellers.emptyTitle} copy={pages.bestsellers.emptyCopy} />;
  }

  if (page === 'authors') {
    const authors = rankAuthorsByActiveListings(listings).slice(0, 24);
    return authors.length ? (
      <div className={css.resultGrid}>
        {authors.map((item, index) => (
          <AuthorCard key={item.name} item={item} rank={index + 1} />
        ))}
      </div>
    ) : (
      <EmptyState
        title="No author activity yet"
        copy="Author trends appear once active book listings include public author metadata."
      />
    );
  }

  if (page === 'author-spotlight') {
    return editorial?.authorSpotlight ? (
      <div className={css.editorialHub}>
        <AuthorSpotlight spotlight={editorial.authorSpotlight} />
        <AuthorsToWatch listings={listings} />
      </div>
    ) : (
      <EmptyState
        title={pages['author-spotlight'].emptyTitle}
        copy={pages['author-spotlight'].emptyCopy}
      />
    );
  }

  if (page === 'series') {
    const series = rankSeriesByActiveListings(listings).slice(0, 24);
    return series.length ? (
      <div className={css.resultGrid}>
        {series.map((item, index) => (
          <SeriesCard key={item.name} item={item} rank={index + 1} />
        ))}
      </div>
    ) : (
      <EmptyState title={pages.series.emptyTitle} copy={pages.series.emptyCopy} />
    );
  }

  const shops = rankShopsByProviderReviews(listings, reviewsByUserId).slice(0, 24);
  return shops.length ? (
    <div className={css.resultGrid}>
      {shops.map((item, index) => (
        <ShopCard key={item.seller.id.uuid} item={item} rank={index + 1} />
      ))}
    </div>
  ) : (
    <EmptyState
      title={pages['highly-rated-shops'].emptyTitle}
      copy={pages['highly-rated-shops'].emptyCopy}
    />
  );
};

const TrendingPageComponent = ({
  params = {},
  listings,
  reviewsByUserId,
  editorial,
  editorialError,
  loading,
  error,
}) => {
  if (!params.page) {
    return <DiscoverLandingPage />;
  }

  const page = params.page;
  const content = pages[page] || pages.hub;

  return (
    <StaticPage title={`${content.title} | PageHop Books`} description={content.intro}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={css.root} id="main-content">
          <LandingPageIntro title={content.title} description={content.intro} />
          <div className={css.inner}>
            {error && page !== 'hub' && page !== 'curated-reads' ? (
              <p className={css.error} role="alert">
                Unable to load Trending right now.
              </p>
            ) : null}
            <Results
              page={page}
              listings={listings}
              reviewsByUserId={reviewsByUserId}
              editorial={editorial}
              editorialError={editorialError}
              loading={loading}
              error={error}
            />
          </div>
        </main>
      </LayoutSingleColumn>
    </StaticPage>
  );
};

const mapStateToProps = state => ({
  listings: state.TrendingPage?.listings || [],
  reviewsByUserId: state.TrendingPage?.reviewsByUserId || {},
  editorial: state.TrendingPage?.editorial || null,
  editorialError: state.TrendingPage?.editorialError || false,
  loading: state.TrendingPage?.loading || false,
  error: state.TrendingPage?.error,
});

export { TrendingPageComponent, pages };
export default connect(mapStateToProps)(TrendingPageComponent);
